import React, { useState, useEffect } from 'react';
import { ProgramCard } from './ProgramCard';
import { useSelector } from 'react-redux';
import { getUser } from '../../../store/userSlice/selectors';
import { PROGRAM_NAMES } from '../../../helpers/constants';
import { useWeb3React } from '@web3-react/core';
import { useLazyQuery } from '@apollo/client';
import { GET_USER_DATA } from '../../../helpers/graphRequests';
import { useDispatch } from 'react-redux';
import { updateUser } from '../../../store/userSlice';
import { usePreviewMode } from '../../../helpers/hooks/useCheckPreviewMode';
import { useTranslation } from 'react-i18next';
import { usePassiveIncome } from '../../../helpers/hooks/usePassiveIncome';
import { useOnchainActiveLevels } from '../../../helpers/hooks/useOnchainActiveLevels';

export const Programs = () => {
  const { t } = useTranslation();
  const currentUser = useSelector(getUser);
  const { account } = useWeb3React();
  const { previewUserAddress } = usePreviewMode();
  const dispatch = useDispatch();
  const [userAddress, setUserAddress] = useState(null);
  const [refetchUser] = useLazyQuery(GET_USER_DATA, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (previewUserAddress) {
      setUserAddress(previewUserAddress?.toLocaleLowerCase());
    } else {
      setUserAddress(account?.toLocaleLowerCase());
    }
  }, [account, previewUserAddress]);

  const { x2: onchainX2, x4: onchainX4, refresh: refreshOnchainLevels } = useOnchainActiveLevels(userAddress);
  const maxActiveLevelX2 = onchainX2 ?? 0;
  const maxActiveLevelX4 = onchainX4 ?? 0;
  const isLevelsReady = onchainX2 !== null && onchainX4 !== null;

  const { getPassiveData, passiveData } = usePassiveIncome();

  useEffect(() => {
    if (account && !passiveData) {
      getPassiveData(account);
    }
  }, [account, passiveData, getPassiveData]);

  const updateInfo = async () => {
    if (!userAddress) return;

    const result = await refetchUser({ variables: { user: userAddress } });
    const user = result?.data?.user;
    if (!user?.id) return;

    dispatch(
      updateUser({
        ...currentUser,
        levelsX2: user.levelsX2,
        levelsX4: user.levelsX4,
        totalRewardX2: user.totalRewardX2,
        totalRewardX4: user.totalRewardX4,
        totalPartners: user.totalPartners,
        totalTeam: user.totalTeam,
        partners24Timestamp: user.partners24Timestamp,
        team24Timestamp: user.team24Timestamp,
      }),
    );

    if (account) {
      getPassiveData(account);
    }

    await refreshOnchainLevels();
  };

  return (
    <div className="flex flex-col space-y-[2.4rem] p-[2rem] rounded-[3rem] border-[0.1rem] border-solid border-white-50 bg-white-40 backdrop-blur-[1.5rem] lg:bg-transparent lg:border-none lg:p-0">
      <span className="text-[2.4rem] font-500 tracking-[-0.48px] text-white lg:px-[1.6rem] ">
        {t('dashboard.programs.title')}
      </span>
      <div className="grid grid-cols-2 gap-[2rem] lg:grid-cols-1 lg:w-full lg:flex lg:overflow-auto lg:w-full hide-scroll lg:gap-[1.2rem] lg:pl-[1.6rem] lg:pr-[1.6rem]">
        <ProgramCard
          type={PROGRAM_NAMES.X2}
          lastIncomeLevel={passiveData?.x2?.lastActiveLevel}
          profit={currentUser?.totalRewardX2}
          maxActiveLevel={maxActiveLevelX2}
          isLevelsReady={isLevelsReady}
          updateInfo={updateInfo}
        />
        <ProgramCard
          type={PROGRAM_NAMES.X4}
          lastIncomeLevel={passiveData?.x4?.lastActiveLevel}
          profit={currentUser?.totalRewardX4}
          maxActiveLevel={maxActiveLevelX4}
          isLevelsReady={isLevelsReady}
          updateInfo={updateInfo}
        />
      </div>
    </div>
  );
};
